/**
 * How a campaign is owned and how people get into it.
 *
 * The same vocabulary drives both entry points — creating a campaign from
 * scratch and dropping a selection into one — because they are the same three
 * questions: what is it called, who owns it, and who joins.
 */

export const CAMPAIGN_SCOPES = {
  justMe: 'justMe',
  organization: 'organization',
} as const

export type CampaignScope =
  (typeof CAMPAIGN_SCOPES)[keyof typeof CAMPAIGN_SCOPES]

/** Which agents an org-owned campaign runs for. */
export const CAMPAIGN_AUDIENCE_SCOPES = {
  allUsers: 'allUsers',
  pickUsers: 'pickUsers',
} as const

export type CampaignAudienceScope =
  (typeof CAMPAIGN_AUDIENCE_SCOPES)[keyof typeof CAMPAIGN_AUDIENCE_SCOPES]

/**
 * Enrolment, when building a campaign from nothing.
 *
 * `filter` and `trigger` are both rolling — people keep joining after the
 * campaign starts — which is what stops a campaign from needing to be
 * recreated every month. `manual` is the only one-shot option.
 */
export const CAMPAIGN_JOIN_MODES = {
  manual: 'manual',
  filter: 'filter',
  trigger: 'trigger',
} as const

export type CampaignJoinMode =
  (typeof CAMPAIGN_JOIN_MODES)[keyof typeof CAMPAIGN_JOIN_MODES]

/** Enrolment, when a selection is being added to a campaign. */
export const CAMPAIGN_ADD_MODES = {
  onlyThese: 'onlyThese',
  keepAdding: 'keepAdding',
} as const

export type CampaignAddMode =
  (typeof CAMPAIGN_ADD_MODES)[keyof typeof CAMPAIGN_ADD_MODES]

/** Where a selection lands. */
export const CAMPAIGN_DESTINATIONS = {
  existing: 'existing',
  new: 'new',
} as const

export type CampaignDestination =
  (typeof CAMPAIGN_DESTINATIONS)[keyof typeof CAMPAIGN_DESTINATIONS]

export const CAMPAIGN_TRIGGERS = {
  whenQuoted: 'whenQuoted',
  dispatched: 'dispatched',
  delivered: 'delivered',
  noOrder60d: 'noOrder60d',
} as const

export type CampaignTrigger =
  (typeof CAMPAIGN_TRIGGERS)[keyof typeof CAMPAIGN_TRIGGERS]

/**
 * What "No order in 60 days" compares against.
 *
 * The service labels `min_age_days` "No activity in", but it reads the quote's
 * own `created_at` — so the chip means "the quote is older than this", not
 * "nothing has happened since".
 */
export const NO_ORDER_MIN_AGE_DAYS = 60

export const CAMPAIGN_TRIGGER_ORDER: readonly CampaignTrigger[] = [
  CAMPAIGN_TRIGGERS.whenQuoted,
  CAMPAIGN_TRIGGERS.dispatched,
  CAMPAIGN_TRIGGERS.delivered,
  CAMPAIGN_TRIGGERS.noOrder60d,
]

/** Which intake channels a trigger listens to. */
export const CAMPAIGN_SOURCE_SCOPES = {
  allSources: 'allSources',
  pickSources: 'pickSources',
} as const

export type CampaignSourceScope =
  (typeof CAMPAIGN_SOURCE_SCOPES)[keyof typeof CAMPAIGN_SOURCE_SCOPES]
